/**
 * Super Admin (DEC-204, UI_SDD §R.15), in the Settings layout: a section list on the left,
 * one URL per section (`/super-admin/:section`).
 *
 * - Pending (DEC-205): website sign-ups waiting for approval. Approve creates the workspace
 *   and emails a sign-in link; Decline emails a short note. Both confirm inline.
 * - Workspaces: every workspace with today's scans against its daily limit; a row opens an
 *   edit panel (daily limit, scans at once, wait between scans, switch off).
 * - New workspace: a name and the first admin's email; sends the normal invitation.
 * - Defaults: the daily limit new workspaces start with.
 * - Sign-ups: new workspaces per day.
 * - Changes: every change made here, with the value before and after.
 *
 * Only a Super Admin reaches it: the menu item appears only for them, this page sends
 * anyone else to Scans, and every API route answers 404 to anyone else.
 * No em-dashes (DEC-069).
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate, useParams } from 'react-router-dom'
import { superAdminApi, type SignupRequest, type WorkspaceSummary, type WorkspaceUpdate } from '../api/superAdmin'
import { useAuth } from '../hooks/useAuth'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/shared/Button'
import { TonePill } from '../components/shared/Badge'
import { PageTitle } from '../components/shared/PageTitle'
import { Spinner } from '../components/shared/Spinner'

const SECTIONS = [
  { id: 'pending', label: 'Pending', intro: 'People who asked for a workspace on the website. Nothing exists until you approve.' },
  { id: 'workspaces', label: 'Workspaces', intro: 'Every workspace, with the scans it started today.' },
  { id: 'new', label: 'New workspace', intro: 'Create a workspace and invite its first admin.' },
  { id: 'defaults', label: 'Defaults', intro: 'What a new workspace starts with.' },
  { id: 'signups', label: 'Sign-ups', intro: 'New workspaces per day, over the last 30 days.' },
  { id: 'changes', label: 'Changes', intro: 'Every change made on this page, newest first.' },
] as const

type SectionId = (typeof SECTIONS)[number]['id']

const inputClass =
  'w-full border border-control rounded-md px-3 py-2 text-[13px] text-ink bg-panel placeholder-faint focus:outline-none focus-visible:shadow-focus disabled:opacity-50'

function fmtDate(iso: string | null): string {
  if (!iso) return 'Never'
  return new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function todayLabel(w: WorkspaceSummary): string {
  return w.daily_scan_limit === null ? `${w.scans_today}, no limit` : `${w.scans_today} of ${w.daily_scan_limit}`
}

function errText(err: unknown): string {
  return err instanceof Error ? err.message : 'Something went wrong.'
}

// ---------------------------------------------------------------------------
// Pending sign-ups (DEC-205)
// ---------------------------------------------------------------------------

function sourceLabel(source: string): string {
  return source === 'google' ? 'Google' : 'Email link'
}

function PendingRow({ r }: { r: SignupRequest }) {
  const qc = useQueryClient()
  const [confirm, setConfirm] = useState<'approve' | 'decline' | null>(null)
  const [note, setNote] = useState('')
  const done = () => {
    void qc.invalidateQueries({ queryKey: ['super-admin'] })
    setConfirm(null)
  }
  const approve = useMutation({ mutationFn: () => superAdminApi.approveSignup(r.signup_id), onSuccess: done })
  const decline = useMutation({
    mutationFn: () => superAdminApi.declineSignup(r.signup_id, note.trim() || null),
    onSuccess: done,
  })
  const error = approve.error ?? decline.error

  return (
    <div className="px-4 py-3 border-b border-hairline last:border-b-0" data-testid="pending-row">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[220px]">
          <div className="text-[13px] text-ink">{r.workspace_name ?? 'No name given'}</div>
          <div className="text-[12px] text-body">{r.email}</div>
          <div className="text-[11px] text-muted mt-0.5">
            {sourceLabel(r.source)} · asked {fmtDate(r.requested_at)}
            {r.from_domain > 1 ? ` · ${r.from_domain} requests from ${r.email_domain}` : ''}
          </div>
        </div>
        {confirm === null && (
          <div className="flex items-center gap-2 flex-none">
            <Button size="sm" onClick={() => setConfirm('approve')}>
              Approve
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setConfirm('decline')}>
              Decline
            </Button>
          </div>
        )}
      </div>

      {confirm === 'approve' && (
        <div role="alertdialog" aria-label="Approve request" className="mt-3 border border-hairline rounded p-3 bg-fill-subtle">
          <p className="text-[12px] text-body mb-2">
            Create {r.workspace_name ?? 'this workspace'} with {r.email} as its admin? They get a sign-in link by
            email. It starts on the Free plan with the default daily scan limit.
          </p>
          <div className="flex gap-2 items-center">
            <Button size="sm" loading={approve.isPending} onClick={() => approve.mutate()}>
              Approve
            </Button>
            <button type="button" onClick={() => setConfirm(null)} className="text-[12px] text-muted hover:text-ink">
              Cancel
            </button>
          </div>
        </div>
      )}

      {confirm === 'decline' && (
        <div role="alertdialog" aria-label="Decline request" className="mt-3 border border-hairline rounded p-3 bg-fill-subtle">
          <label htmlFor={`note-${r.signup_id}`} className="block text-[12px] text-body mb-1">
            Note for {r.email} (optional)
          </label>
          <textarea
            id={`note-${r.signup_id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={500}
            rows={2}
            className={inputClass}
          />
          <p className="text-[11px] text-muted mt-1 mb-2">They are told by email and can ask again after 30 days.</p>
          <div className="flex gap-2 items-center">
            <Button size="sm" variant="secondary" loading={decline.isPending} onClick={() => decline.mutate()}>
              Decline
            </Button>
            <button type="button" onClick={() => setConfirm(null)} className="text-[12px] text-muted hover:text-ink">
              Cancel
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-[12px] text-critical mt-2">{errText(error)}</p>}
    </div>
  )
}

function PendingSection() {
  const [view, setView] = useState<'pending' | 'decided'>('pending')
  const list = useQuery({
    queryKey: ['super-admin', 'signup-requests', view],
    queryFn: () => superAdminApi.signupRequests(view === 'pending' ? 'pending' : 'all'),
  })
  const rows = (list.data?.requests ?? []).filter((r) => (view === 'pending' ? r.status === 'pending' : r.status !== 'pending'))

  return (
    <>
      <div className="flex gap-1 mb-4" role="group" aria-label="Which requests">
        {(['pending', 'decided'] as const).map((v) => (
          <button
            key={v}
            type="button"
            aria-pressed={view === v}
            onClick={() => setView(v)}
            className={`px-3 py-[6px] rounded-full text-[12px] font-semibold leading-none transition-colors ${
              view === v ? 'bg-accent text-white' : 'bg-accent/[0.12] text-body hover:bg-accent/[0.2]'
            }`}
          >
            {v === 'pending' ? `Waiting${list.data ? ` (${list.data.pending_count})` : ''}` : 'Decided'}
          </button>
        ))}
      </div>
      {list.isLoading ? (
        <Spinner />
      ) : list.isError ? (
        <p className="text-[13px] text-critical">{errText(list.error)}</p>
      ) : rows.length === 0 ? (
        <p className="text-[13px] text-muted">
          {view === 'pending' ? 'Nobody is waiting. New requests appear here and you get an email.' : 'No decisions yet.'}
        </p>
      ) : view === 'pending' ? (
        <div className="bg-panel border border-hairline rounded-md" data-testid="pending-requests">
          {rows.map((r) => (
            <PendingRow key={r.signup_id} r={r} />
          ))}
        </div>
      ) : (
        <div className="bg-panel border border-hairline rounded-md divide-y divide-hairline" data-testid="decided-requests">
          {rows.map((r) => (
            <div key={r.signup_id} className="px-4 py-2.5 flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[220px]">
                <div className="text-[13px] text-ink">{r.workspace_name ?? 'No name given'}</div>
                <div className="text-[11px] text-muted">
                  {r.email} · {fmtDate(r.decided_at)} · {r.decided_by ?? ''}
                  {r.decline_note ? ` · "${r.decline_note}"` : ''}
                </div>
              </div>
              {r.status === 'approved' ? (
                <TonePill tone="success">Approved</TonePill>
              ) : (
                <TonePill tone="stopped">Declined</TonePill>
              )}
            </div>
          ))}
        </div>
      )}
    </>
  )
}

// ---------------------------------------------------------------------------
// Workspaces
// ---------------------------------------------------------------------------

function EditPanel({ w, onDone }: { w: WorkspaceSummary; onDone: () => void }) {
  const qc = useQueryClient()
  const [noLimit, setNoLimit] = useState(w.daily_scan_limit === null)
  const [daily, setDaily] = useState(String(w.daily_scan_limit ?? 10))
  const [concurrent, setConcurrent] = useState(String(w.max_concurrent_scans))
  const [waitMinutes, setWaitMinutes] = useState(String(w.min_scan_interval_minutes))
  const [confirmOff, setConfirmOff] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const save = useMutation({
    mutationFn: (body: WorkspaceUpdate) => superAdminApi.updateWorkspace(w.tenant_id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-admin'] })
      setError(null)
      setConfirmOff(false)
    },
    onError: (err) => setError(errText(err)),
  })

  function handleSave(e: React.FormEvent) {
    e.preventDefault()
    const body: WorkspaceUpdate = { clear: [] }
    if (noLimit) body.clear!.push('daily_scan_limit')
    else body.daily_scan_limit = Number(daily)
    if (Number(concurrent) !== w.max_concurrent_scans) body.max_concurrent_scans = Number(concurrent)
    if (Number(waitMinutes) !== w.min_scan_interval_minutes) body.min_scan_interval_minutes = Number(waitMinutes)
    save.mutate(body)
  }

  const off = w.disabled_at !== null

  return (
    <div className="px-4 py-4 bg-fill-subtle border-t border-hairline" data-testid="workspace-edit">
      <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-3">
        <div>
          <label htmlFor={`daily-${w.tenant_id}`} className="block text-[12px] font-medium text-body mb-1">
            Scans per day
          </label>
          <input
            id={`daily-${w.tenant_id}`}
            type="number"
            min={0}
            value={daily}
            disabled={noLimit}
            onChange={(e) => setDaily(e.target.value)}
            className={inputClass}
          />
          <label className="mt-2 flex items-center gap-2 text-[12px] text-muted">
            <input type="checkbox" checked={noLimit} onChange={(e) => setNoLimit(e.target.checked)} />
            No limit
          </label>
        </div>
        <div>
          <label htmlFor={`conc-${w.tenant_id}`} className="block text-[12px] font-medium text-body mb-1">
            Scans at once
          </label>
          <input
            id={`conc-${w.tenant_id}`}
            type="number"
            min={1}
            value={concurrent}
            onChange={(e) => setConcurrent(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={`wait-${w.tenant_id}`} className="block text-[12px] font-medium text-body mb-1">
            Minutes between scans of a branch
          </label>
          <input
            id={`wait-${w.tenant_id}`}
            type="number"
            min={0}
            value={waitMinutes}
            onChange={(e) => setWaitMinutes(e.target.value)}
            className={inputClass}
          />
        </div>
        <div className="sm:col-span-3 flex flex-wrap items-center gap-2">
          <Button type="submit" size="sm" loading={save.isPending}>
            Save
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={onDone}>
            Close
          </Button>
          <span className="flex-1" />
          {!w.is_own &&
            (off ? (
              <Button type="button" size="sm" variant="secondary" onClick={() => save.mutate({ disabled: false })}>
                Switch on
              </Button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmOff(true)}
                className="text-[12px] text-muted hover:text-critical transition-colors"
              >
                Switch off
              </button>
            ))}
        </div>
      </form>
      {confirmOff && (
        <div role="alertdialog" className="mt-3 border border-hairline rounded p-3 bg-panel">
          <p className="text-[12px] text-body mb-2">
            Switch off {w.name}? Its members are signed out at once and it cannot start scans until you switch
            it back on. Nothing is deleted.
          </p>
          <div className="flex gap-2">
            <Button size="sm" loading={save.isPending} onClick={() => save.mutate({ disabled: true })}>
              Switch off
            </Button>
            <button type="button" onClick={() => setConfirmOff(false)} className="text-[12px] text-muted hover:text-ink">
              Keep it on
            </button>
          </div>
        </div>
      )}
      {error && <p className="text-[12px] text-critical mt-2">{error}</p>}
    </div>
  )
}

function WorkspacesSection() {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState<string | null>(null)
  const list = useQuery({ queryKey: ['super-admin', 'workspaces'], queryFn: superAdminApi.listWorkspaces })
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const all = list.data?.workspaces ?? []
    if (!needle) return all
    return all.filter(
      (w) =>
        w.name.toLowerCase().includes(needle) ||
        (w.first_admin_email ?? '').toLowerCase().includes(needle),
    )
  }, [list.data, q])

  if (list.isLoading) return <Spinner />
  if (list.isError) return <p className="text-[13px] text-critical">{errText(list.error)}</p>

  return (
    <>
      <input
        type="search"
        aria-label="Search workspaces"
        placeholder="Search by name or admin email"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className={`${inputClass} mb-4 max-w-[360px]`}
      />
      <div className="bg-panel border border-hairline rounded-md overflow-hidden" data-testid="super-admin-workspaces">
        <div className="hidden md:grid grid-cols-[minmax(0,2.2fr)_70px_100px_120px_100px_90px] gap-3 px-4 py-2 text-[11px] text-muted border-b border-hairline">
          <span>Workspace</span>
          <span className="text-right">Members</span>
          <span>Created</span>
          <span className="text-right">Scans today</span>
          <span>Last scan</span>
          <span>Status</span>
        </div>
        {shown.map((w) => (
          <div key={w.tenant_id} className="border-b border-hairline last:border-b-0">
            <button
              type="button"
              aria-expanded={open === w.tenant_id}
              onClick={() => setOpen(open === w.tenant_id ? null : w.tenant_id)}
              className="w-full grid grid-cols-1 md:grid-cols-[minmax(0,2.2fr)_70px_100px_120px_100px_90px] gap-1 md:gap-3 px-4 py-2.5 text-left text-[13px] hover:bg-fill-subtle transition-colors items-center"
            >
              <span className="min-w-0">
                <span className="block text-ink truncate">
                  {w.name}
                  {w.is_own && <span className="text-muted"> (yours)</span>}
                </span>
                <span className="block text-[11px] text-muted truncate">
                  {w.first_admin_email ?? 'No admin with an email'}
                  {' · '}
                  {w.plan === 'free' ? 'Free' : w.plan}
                  {w.created_via === 'signup' ? ' · signed up' : ''}
                </span>
              </span>
              <span className="md:text-right tabular-nums text-body">{w.members}</span>
              <span className="text-body">{fmtDate(w.created_at)}</span>
              <span className="md:text-right tabular-nums text-body">{todayLabel(w)}</span>
              <span className="text-body">{fmtDate(w.last_scan_at)}</span>
              <span>
                {w.disabled_at ? (
                  <TonePill tone="stopped">Off</TonePill>
                ) : (
                  <TonePill tone="success">Active</TonePill>
                )}
              </span>
            </button>
            {open === w.tenant_id && <EditPanel w={w} onDone={() => setOpen(null)} />}
          </div>
        ))}
        {shown.length === 0 && <div className="px-4 py-3 text-[12px] text-muted">No workspace matches.</div>}
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// New workspace
// ---------------------------------------------------------------------------

function NewWorkspaceSection() {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [copied, setCopied] = useState(false)
  const create = useMutation({
    mutationFn: () => superAdminApi.createWorkspace(name.trim(), email.trim()),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-admin'] })
      setCopied(false)
    },
  })

  return (
    <div className="bg-panel border border-hairline rounded-md p-5 max-w-[520px]">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (name.trim() && email.trim()) create.mutate()
        }}
        className="space-y-4"
      >
        <div>
          <label htmlFor="new-ws-name" className="block text-[12px] font-medium text-body mb-1">
            Workspace name
          </label>
          <input id="new-ws-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} className={inputClass} />
        </div>
        <div>
          <label htmlFor="new-ws-email" className="block text-[12px] font-medium text-body mb-1">
            First admin email
          </label>
          <input
            id="new-ws-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@company.com"
            className={inputClass}
          />
        </div>
        <Button type="submit" loading={create.isPending} disabled={!name.trim() || !email.trim()}>
          Send invitation
        </Button>
      </form>
      {create.isError && <p className="text-[12px] text-critical mt-3">{errText(create.error)}</p>}
      {create.data && (
        <div className="mt-4 border border-hairline rounded p-3 bg-fill-subtle" data-testid="new-workspace-result">
          <p className="text-[12px] text-body m-0">
            {create.data.workspace.name} was created and an invitation went to {email.trim()}.
          </p>
          {!create.data.email_sent && (
            <>
              <p className="text-[11px] text-muted mt-2 mb-1">{create.data.email_notice}</p>
              <div className="flex items-center gap-2">
                <input readOnly value={create.data.invite_url} aria-label="Invitation link" className={inputClass} />
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(create.data!.invite_url).then(() => setCopied(true))
                  }}
                  className="text-[12px] text-accent-text flex-none"
                >
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

function DefaultsSection() {
  const qc = useQueryClient()
  const defaults = useQuery({ queryKey: ['super-admin', 'defaults'], queryFn: superAdminApi.getDefaults })
  const [value, setValue] = useState<string | null>(null)
  const [noLimit, setNoLimit] = useState<boolean | null>(null)
  const save = useMutation({
    mutationFn: (v: number | null) => superAdminApi.setDefaults(v),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['super-admin'] })
      setValue(null)
      setNoLimit(null)
    },
  })
  if (defaults.isLoading || !defaults.data) return <Spinner />
  const current = defaults.data.default_daily_scan_limit
  const shownNoLimit = noLimit ?? current === null
  const shownValue = value ?? String(current ?? 10)

  return (
    <div className="bg-panel border border-hairline rounded-md p-5 max-w-[520px]">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          save.mutate(shownNoLimit ? null : Number(shownValue))
        }}
        className="space-y-3"
      >
        <label htmlFor="default-daily" className="block text-[12px] font-medium text-body">
          Scans per day for a new workspace
        </label>
        <input
          id="default-daily"
          type="number"
          min={0}
          value={shownValue}
          disabled={shownNoLimit}
          onChange={(e) => setValue(e.target.value)}
          className={`${inputClass} max-w-[160px]`}
        />
        <label className="flex items-center gap-2 text-[12px] text-muted">
          <input type="checkbox" checked={shownNoLimit} onChange={(e) => setNoLimit(e.target.checked)} />
          No limit
        </label>
        <p className="text-[11px] text-muted m-0">
          Applies to workspaces created from now on. Existing workspaces keep their own limit; change those on
          Workspaces.
        </p>
        <Button type="submit" size="sm" loading={save.isPending}>
          Save
        </Button>
      </form>
      {save.isError && <p className="text-[12px] text-critical mt-2">{errText(save.error)}</p>}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sign-ups
// ---------------------------------------------------------------------------

function SignupsSection() {
  const data = useQuery({ queryKey: ['super-admin', 'signups'], queryFn: () => superAdminApi.signups(30) })
  if (data.isLoading || !data.data) return <Spinner />
  const days = data.data.days
  const max = Math.max(1, ...days.map((d) => d.workspaces))
  return (
    <div className="bg-panel border border-hairline rounded-md p-5" data-testid="super-admin-signups">
      <div className="grid grid-cols-2 gap-3 mb-4 max-w-[360px]">
        <div>
          <div className="text-[11px] text-muted">New workspaces</div>
          <div className="text-[22px] font-medium text-ink tabular-nums">{data.data.total}</div>
        </div>
        <div>
          <div className="text-[11px] text-muted">Signed up themselves</div>
          <div className="text-[22px] font-medium text-ink tabular-nums">{data.data.self_serve_total}</div>
        </div>
      </div>
      <ul className="m-0 p-0 list-none flex items-end gap-[3px] h-[100px]" aria-label="New workspaces per day">
        {days.map((d) => (
          <li key={d.date} className="flex-1 min-w-0 h-full flex items-end">
            <span
              className="w-full rounded-t-sm bg-accent/70"
              style={{ height: `${Math.max(2, (d.workspaces / max) * 100)}%` }}
              title={`${d.date}: ${d.workspaces} new, ${d.self_serve} signed up themselves`}
            />
          </li>
        ))}
      </ul>
      <div className="flex justify-between text-[10px] text-faint mt-1">
        <span>{days[0]?.date}</span>
        <span>{days[days.length - 1]?.date}</span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Changes
// ---------------------------------------------------------------------------

const ACTION_LABEL: Record<string, string> = {
  update_workspace: 'Changed a workspace',
  update_limits: 'Changed limits in Settings',
  create_workspace: 'Created a workspace',
  update_defaults: 'Changed the defaults',
  approve_signup: 'Approved a sign-up',
  decline_signup: 'Declined a sign-up',
}

function ChangesSection() {
  const data = useQuery({ queryKey: ['super-admin', 'audit'], queryFn: () => superAdminApi.audit(50) })
  if (data.isLoading || !data.data) return <Spinner />
  const entries = data.data.entries
  if (entries.length === 0) return <p className="text-[13px] text-muted">No changes yet.</p>
  return (
    <div className="bg-panel border border-hairline rounded-md divide-y divide-hairline">
      {entries.map((e) => (
        <div key={`${e.at}-${e.action}-${e.tenant_id}`} className="px-4 py-2.5">
          <div className="text-[13px] text-ink">
            {ACTION_LABEL[e.action] ?? e.action}
            {e.workspace_name ? `: ${e.workspace_name}` : ''}
          </div>
          <div className="text-[11px] text-muted">
            {new Date(e.at.endsWith('Z') ? e.at : `${e.at}Z`).toLocaleString()} · {e.actor_email}
          </div>
          {e.after && (
            <div className="text-[11px] text-muted mt-1 tabular-nums">
              {Object.entries(e.after)
                .map(([k, v]) => `${k.replace(/_/g, ' ')}: ${e.before ? `${String(e.before[k] ?? 'none')} to ` : ''}${String(v ?? 'none')}`)
                .join(' · ')}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function SuperAdminPage() {
  const { claims } = useAuth()
  const { section } = useParams()
  const pending = useQuery({
    queryKey: ['super-admin', 'signup-requests', 'pending'],
    queryFn: () => superAdminApi.signupRequests('pending'),
    enabled: Boolean(claims?.is_super_admin),
  })
  if (!claims?.is_super_admin) return <Navigate to="/scans" replace />
  const current = SECTIONS.find((s) => s.id === section)
  if (!current) return <Navigate to="/super-admin/pending" replace />
  const id: SectionId = current.id

  return (
    <AppShell crumb={`Super Admin / ${current.label}`} active="super-admin">
      <div className="px-8 py-6">
        <PageTitle eyebrow="Athenaworks" className="mb-6">
          Super Admin
        </PageTitle>
        <div className="flex flex-col md:flex-row gap-6 md:gap-8">
          <nav aria-label="Super Admin sections" className="md:w-[180px] flex-none" data-testid="super-admin-nav">
            <ul className="m-0 p-0 list-none flex md:flex-col flex-wrap gap-1">
              {SECTIONS.map((s) => {
                const on = s.id === id
                return (
                  <li key={s.id}>
                    <Link
                      to={`/super-admin/${s.id}`}
                      aria-current={on ? 'page' : undefined}
                      className={`block px-3 py-1.5 rounded-md text-[13px] font-medium transition-colors ${
                        on ? 'bg-surface-active text-ink' : 'text-muted hover:text-body hover:bg-fill-subtle'
                      }`}
                    >
                      {s.label}
                      {s.id === 'pending' && (pending.data?.pending_count ?? 0) > 0 && (
                        <span className="ml-2 inline-flex min-w-[18px] justify-center rounded-full bg-accent px-1.5 text-[11px] font-semibold leading-[18px] text-white tabular-nums">
                          {pending.data!.pending_count}
                        </span>
                      )}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>
          <section aria-labelledby="super-admin-title" className="flex-1 min-w-0 max-w-[960px]">
            <h2 id="super-admin-title" className="text-[18px] font-semibold text-ink m-0">
              {current.label}
            </h2>
            <p className="text-[13px] text-muted mt-1 mb-5">{current.intro}</p>
            {id === 'pending' && <PendingSection />}
            {id === 'workspaces' && <WorkspacesSection />}
            {id === 'new' && <NewWorkspaceSection />}
            {id === 'defaults' && <DefaultsSection />}
            {id === 'signups' && <SignupsSection />}
            {id === 'changes' && <ChangesSection />}
          </section>
        </div>
      </div>
    </AppShell>
  )
}
